import type { ConsultaDisponivel, FerramentaExecutada, FerramentaSolicitada } from '@/types/api';

// Ponte com o +Vidas (a aplicacao onde este chat esta embutido).
//
// Por que passar pelo navegador em vez de a API consultar o banco: as consultas
// de dados rodam DENTRO do +Vidas, com o cookie de sessao do usuario, e o RBAC
// do modulo (permissao, pre-filtro por perfil, unidade) continua sendo o unico
// caminho. A API da assistente orquestra o modelo e nunca toca dado de cliente.
//
// Tudo aqui degrada em silencio: se a versao publicada do +Vidas ainda nao tiver
// os endpoints, o catalogo vem vazio e o chat segue funcionando so' com a
// documentacao, como antes.

interface RespostaContexto {
  ok: boolean;
  usuarioId?: number;
  unidadeId?: number;
  controllers?: string[];
  erro?: string;
}

interface RespostaCatalogo {
  ok: boolean;
  consultas?: ConsultaDisponivel[];
  erro?: string;
}

// O +Vidas pode rodar em subdiretorio, entao a base vem de Page.AbsoluteUrl
// (global do jquery.Easy.js na pagina). Sem ela, cai na origem atual.
function baseVidas(): string {
  const page = (window as any).Page;
  const absoluta = page && typeof page.AbsoluteUrl === 'string' ? page.AbsoluteUrl : '';
  if (absoluta) return absoluta.replace(/\/$/, '');
  return window.location.origin.replace(/\/$/, '');
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const resp = await fetch(`${baseVidas()}${path}`, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include'
    });
    if (!resp.ok) return null;
    return (await resp.json()) as T;
  } catch {
    return null;
  }
}

let cacheControllers: string[] | null = null;
let cacheConsultas: ConsultaDisponivel[] | null = null;

/** Controllers em que o usuario tem leitura. Filtra o que entra no prompt. */
export async function carregarControllersPermitidos(): Promise<string[]> {
  if (cacheControllers) return cacheControllers;

  const r = await getJson<RespostaContexto>('/AssistenteIADados/ContextoPermissao');
  cacheControllers = r && r.ok && Array.isArray(r.controllers) ? r.controllers : [];
  return cacheControllers;
}

/** Consultas de dados que este usuario pode executar (ja filtradas por permissao). */
export async function carregarConsultasDisponiveis(): Promise<ConsultaDisponivel[]> {
  if (cacheConsultas) return cacheConsultas;

  const r = await getJson<RespostaCatalogo>('/AssistenteIADados/Catalogo');
  cacheConsultas = r && r.ok && Array.isArray(r.consultas) ? r.consultas : [];
  return cacheConsultas;
}

/** Descarta o que foi carregado (troca de usuario ou de unidade na mesma aba). */
export function limparCacheDados() {
  cacheControllers = null;
  cacheConsultas = null;
}

const PREFIXO_FERRAMENTA = 'consultar_';
const FERRAMENTA_RESOLVER = 'resolver_entidade';
const FERRAMENTA_OPCOES = 'buscar_opcao_de_filtro';

/**
 * POST de uma ferramenta fixa no +Vidas. O payload inteiro volta como
 * tool_result, inclusive o "motivo" de uma lista vazia: o modelo precisa
 * distinguir "nao existe" de "voce nao pode ver".
 */
async function postarNoVidas(
  caminho: string,
  form: URLSearchParams,
  base: FerramentaExecutada,
  falha: string
): Promise<FerramentaExecutada> {
  try {
    const resp = await fetch(`${baseVidas()}${caminho}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: form.toString(),
      credentials: 'include'
    });

    if (!resp.ok) return { ...base, erro: `${falha} (erro ${resp.status}).` };

    const payload = await resp.json();
    if (!payload || payload.ok !== true) {
      return { ...base, erro: String(payload?.erro ?? falha) };
    }

    return { ...base, resultadoJson: JSON.stringify(payload) };
  } catch (e) {
    return { ...base, erro: e instanceof Error ? e.message : falha };
  }
}

/** Resolve nome em ID no +Vidas, com o gate de lookup (Viewer) do modulo dono. */
function executarResolver(
  argumentos: Record<string, unknown>,
  base: FerramentaExecutada
): Promise<FerramentaExecutada> {
  const form = new URLSearchParams();
  form.set('tipo', String(argumentos.tipo ?? ''));
  form.set('termo', String(argumentos.termo ?? ''));
  if (argumentos.incluirInativos === true) form.set('incluirInativos', 'true');

  return postarNoVidas(
    '/AssistenteIADados/Resolver',
    form,
    base,
    'Não foi possível pesquisar o cadastro'
  );
}

/**
 * Acha o ID de uma opcao de filtro de lista pelo texto. Vale para os filtros
 * cuja lista e' grande demais para ir no prompt: o ID vem do datasource do
 * proprio filtro, que e' o unico valor que aquele recorte aceita.
 */
function executarBuscaDeOpcoes(
  argumentos: Record<string, unknown>,
  base: FerramentaExecutada
): Promise<FerramentaExecutada> {
  const form = new URLSearchParams();
  form.set('codigo', String(argumentos.consulta ?? argumentos.codigo ?? ''));
  form.set('filtroId', String(argumentos.filtroId ?? ''));
  form.set('termo', String(argumentos.termo ?? ''));

  return postarNoVidas(
    '/AssistenteIADados/OpcoesFiltro',
    form,
    base,
    'Não foi possível carregar as opções do filtro'
  );
}

/**
 * Executa no +Vidas uma consulta que o modelo pediu.
 *
 * Traducao dos argumentos: chave f_<id> e' filtro do catalogo Seg.ControllerFiltro
 * e vai como esta; "justificativa" tem campo proprio; qualquer outra chave e'
 * parametro da procedure e vai como p_<nome>. O que o modelo inventar fora do
 * declarado e' descartado no servidor, nao aqui.
 */
export async function executarFerramenta(
  ferramenta: FerramentaSolicitada,
  sessaoId: string | null
): Promise<FerramentaExecutada> {
  const nome = ferramenta.nome ?? '';
  const codigo = nome.toLowerCase().startsWith(PREFIXO_FERRAMENTA)
    ? nome.substring(PREFIXO_FERRAMENTA.length)
    : nome;

  const base: FerramentaExecutada = {
    id: ferramenta.id,
    nome,
    argumentosJson: ferramenta.argumentosJson,
    resultadoJson: null,
    erro: null
  };

  let argumentos: Record<string, unknown> = {};
  try {
    argumentos = ferramenta.argumentosJson ? JSON.parse(ferramenta.argumentosJson) : {};
  } catch {
    return { ...base, erro: 'Não foi possível interpretar os parâmetros da consulta.' };
  }

  // Ferramentas fixas tem endpoint proprio: nao sao consultas do catalogo.
  if (nome.toLowerCase() === FERRAMENTA_RESOLVER) {
    return executarResolver(argumentos, base);
  }

  if (nome.toLowerCase() === FERRAMENTA_OPCOES) {
    return executarBuscaDeOpcoes(argumentos, base);
  }

  const form = new URLSearchParams();
  form.set('codigo', codigo);
  if (sessaoId) form.set('sessaoId', sessaoId);

  for (const [chave, valor] of Object.entries(argumentos)) {
    if (valor === null || valor === undefined || valor === '') continue;

    if (/^f_\d+$/i.test(chave)) {
      form.set(chave, String(valor));
    } else if (chave.toLowerCase() === 'justificativa') {
      form.set('justificativa', String(valor));
    } else {
      form.set(`p_${chave}`, String(valor));
    }
  }

  try {
    const resp = await fetch(`${baseVidas()}/AssistenteIADados/Executar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: form.toString(),
      credentials: 'include'
    });

    if (!resp.ok) {
      return { ...base, erro: `A consulta não pôde ser executada (erro ${resp.status}).` };
    }

    const payload = await resp.json();

    // Negativa do servidor (sem permissão, parâmetro inválido, filtro grande
    // demais) volta como erro para o modelo, com o motivo: ele precisa dizer
    // que não houve dado em vez de preencher a lacuna.
    if (!payload || payload.ok !== true) {
      return { ...base, erro: String(payload?.erro ?? 'Consulta não disponível.') };
    }

    return { ...base, resultadoJson: JSON.stringify(payload) };
  } catch (e) {
    return {
      ...base,
      erro: e instanceof Error ? e.message : 'Falha ao executar a consulta.'
    };
  }
}
